import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ban, Check, Copy, KeyRound, Mail, NotebookPen, Search, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";
import { AddUserModal, DeleteUserModal, LoginStatsPill, ResetPasswordModal, SessionStatusPill, StatCard } from "../shared";
import ProlificStudyModal from "@/components/admin/ProlificStudyModal";

const TABS = [
    { v: "waitlist", label: "Waitlist" },
    { v: "approved", label: "Approved" },
    { v: "suspended", label: "Suspended" },
    { v: "denied", label: "Denied" },
    { v: "all", label: "All" },
];

/* ----------------------- Applications view ----------------------- */

export default function ApplicationsView() {
    const [tab, setTab] = useState("waitlist");
    const [data, setData] = useState({ items: [], counts: { waitlist: 0, approved: 0, denied: 0 } });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [openId, setOpenId] = useState(null);
    const [noteDraft, setNoteDraft] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [prolificOpen, setProlificOpen] = useState(false);

    const load = async () => {
        try {
            const r = await api.get(`/admin/applications`, { params: { status: tab } });
            setData(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let active = true;
        const run = async () => {
            try {
                const r = await api.get(`/admin/applications`, { params: { status: tab } });
                if (active) setData(r.data);
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const items = data.items.filter(it =>
        !q ||
        (it.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (it.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (it.biggest_challenge || "").toLowerCase().includes(q.toLowerCase())
    );

    const act = async (id, action) => {
        try {
            await api.post(`/admin/applications/${id}/${action}`, { notes: noteDraft });
            const verb = {
                approve: "Approved.",
                deny: "Denied.",
                suspend: "Access suspended.",
            }[action] || "Done.";
            toast.success(verb);
            setOpenId(null);
            setNoteDraft("");
            load();
        } catch {
            toast.error("Something went wrong.");
        }
    };

    const saveNotes = async (id) => {
        try {
            await api.post(`/admin/applications/${id}/notes`, { notes: noteDraft });
            toast.success("Notes saved.");
            load();
        } catch {
            toast.error("Couldn't save notes.");
        }
    };

    const copyLoginInfo = (item) => {
        const text = `Hi ${item.name.split(" ")[0] || ""},\n\nYou're in. Memory Lane is ready when you are.\n\nSign in: ${window.location.origin}/login\nEmail: ${item.email}\nPassword: (the one you chose when you applied)\n\nIf you've forgotten your password, reply and we'll reset it.\n\nWelcome to the journey,\nMemory Lane`;
        navigator.clipboard.writeText(text).then(
            () => toast.success("Copied login instructions to clipboard."),
            () => toast.error("Copy failed.")
        );
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-3">
                <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">Applications</h1>
                <button
                    onClick={() => setAddOpen(true)}
                    data-testid="admin-add-user-open"
                    className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-4 py-2 text-sm font-medium"
                >
                    <UserPlus size={14} strokeWidth={2} /> Add user manually
                </button>
                <button
                    onClick={async () => {
                        const n = parseInt(window.prompt("How many tester links to generate? (1–50)", "10"), 10);
                        if (!n || n < 1) return;
                        const label = window.prompt("Label for this batch (optional, e.g. 'User Interviews — March'):", "") || "";
                        try {
                            const r = await api.post("/admin/testers/generate", { count: Math.min(50, n), label });
                            const urls = r.data.created.map(c => `${c.code}: ${c.url}`).join("\n");
                            await navigator.clipboard.writeText(urls);
                            toast.success(`Generated ${r.data.count} tester links — copied to clipboard.`);
                        } catch {
                            toast.error("Couldn't generate. Try again.");
                        }
                    }}
                    data-testid="admin-generate-testers"
                    className="inline-flex items-center gap-2 bg-white border border-teal text-teal-dark hover:bg-teal-soft rounded-2xl px-4 py-2 text-sm font-medium"
                >
                    <UserPlus size={14} strokeWidth={2} /> Generate tester links
                </button>
                <button
                    onClick={() => setProlificOpen(true)}
                    data-testid="admin-prolific-health-open"
                    title="Diagnose + reset the Prolific study link"
                    className="inline-flex items-center gap-2 bg-white border border-teal text-teal-dark hover:bg-teal-soft rounded-2xl px-4 py-2 text-sm font-medium"
                >
                    <ShieldCheck size={14} strokeWidth={2} /> Prolific study
                </button>
            </div>

            <ProlificStudyModal open={prolificOpen} onClose={() => setProlificOpen(false)} />

            {addOpen && (
                <AddUserModal
                    onClose={() => setAddOpen(false)}
                    onCreated={() => { setAddOpen(false); load(); }}
                />
            )}

            {resetUser && (
                <ResetPasswordModal
                    user={resetUser}
                    onClose={() => setResetUser(null)}
                />
            )}

            {deleteUser && (
                <DeleteUserModal
                    user={deleteUser}
                    onClose={() => setDeleteUser(null)}
                    onDeleted={() => { setDeleteUser(null); load(); }}
                />
            )}

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5" data-testid="admin-counts">
                <StatCard label="Waitlist" value={data.counts.waitlist} tone="yellow" />
                <StatCard label="Approved" value={data.counts.approved} tone="teal" />
                <StatCard label="Suspended" value={data.counts.suspended || 0} tone="lavender" />
                <StatCard label="Denied" value={data.counts.denied} tone="coral" />
            </div>

            {/* Session migration progress — visible only on Approved tab so it doesn't clutter waitlist triage */}
            {tab === "approved" && (() => {
                const approved = data.items.filter(it => it.status === "approved");
                const synced = approved.filter(it => it.primary_session_id).length;
                const pending = approved.length - synced;
                const pct = approved.length ? Math.round((synced / approved.length) * 100) : 0;
                return (
                    <section
                        className="ml-card p-4 mt-3 bg-stage_early/30 !border-stage_early/40 flex flex-wrap items-center gap-3"
                        data-testid="admin-session-migration"
                    >
                        <div className="flex-1 min-w-[200px]">
                            <p className="text-xs uppercase tracking-wider font-semibold text-stage_early-dark">
                                Session migration
                            </p>
                            <p className="text-sm text-[#1F2624] mt-1">
                                <strong>{synced}</strong> of <strong>{approved.length}</strong> approved users have a stable session_id ({pct}%).
                                {pending > 0 && (
                                    <>  <span className="text-[#4C5551]">{pending} will auto-sync on next login.</span></>
                                )}
                            </p>
                        </div>
                        <div className="w-full md:w-48 h-3 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-stage_early-dark transition-all" style={{ width: `${pct}%` }} />
                        </div>
                    </section>
                );
            })()}

            {/* Tabs + search */}
            <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                    {TABS.map(t => (
                        <button key={t.v} onClick={() => setTab(t.v)} data-testid={`admin-tab-${t.v}`}
                            className={`px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${tab === t.v
                                ? "bg-teal text-white border-teal"
                                : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                }`}>{t.label}</button>
                    ))}
                </div>
                <div className="relative">
                    <Search size={16} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7A74]" />
                    <input type="search" value={q} onChange={e => setQ(e.target.value)}
                        data-testid="admin-search"
                        placeholder="Search email, name, challenge…"
                        className="ml-input !min-h-[40px] !py-2 !pl-9 !pr-3 text-sm w-72" />
                </div>
            </div>

            {/* List */}
            <section className="mt-6 space-y-3" data-testid="admin-list">
                {loading && <p className="text-[#4C5551]">Loading…</p>}
                {!loading && items.length === 0 && (
                    <p className="text-[#4C5551] py-8 text-center bg-white rounded-2xl">No applications match.</p>
                )}
                {items.map(it => {
                    const isOpen = openId === it.id;
                    return (
                        <article key={it.id} className="ml-card p-5" data-testid={`admin-item-${it.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="font-heading font-medium text-[#1F2624]">{it.name}</h3>
                                        <span className={`text-xs px-2.5 py-0.5 rounded-2xl font-medium ${it.status === "waitlist" ? "bg-yellow-soft text-yellow-dark" :
                                            it.status === "approved" ? "bg-teal-soft text-teal-dark" :
                                                it.status === "suspended" ? "bg-stage_late-light text-stage_late-dark" :
                                                    "bg-coral-light text-coral-dark"
                                            }`}>{it.status}</span>
                                        {it.relationship && (
                                            <span className="text-xs px-2.5 py-0.5 rounded-2xl bg-cream-dark text-[#4C5551]">{it.relationship.replace(/_/g, ' ')}</span>
                                        )}
                                        {it.status === "approved" && (
                                            <SessionStatusPill user={it} />
                                        )}
                                        {it.status === "approved" && (
                                            <LoginStatsPill user={it} />
                                        )}
                                        {it.participant_id && (
                                            <span
                                                data-testid={`admin-participant-id-${it.id}`}
                                                title="User Interviews participant ID"
                                                className="text-xs px-2.5 py-0.5 rounded-2xl bg-stage_late-light text-stage_late-dark font-mono"
                                            >
                                                UID: {it.participant_id}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-[#4C5551] mt-0.5">{it.email}</p>
                                    {it.biggest_challenge && (
                                        <p className="text-[#1F2624] mt-2 leading-relaxed">
                                            <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Challenge:</span>{" "}
                                            {it.biggest_challenge}
                                        </p>
                                    )}
                                    {it.admin_notes && (
                                        <p className="text-sm text-teal-dark mt-2 bg-teal-soft/60 rounded-lg px-3 py-2">
                                            <strong>Note:</strong> {it.admin_notes}
                                        </p>
                                    )}
                                    <p className="text-xs text-[#6E7A74] mt-2">
                                        Applied {new Date(it.created_at).toLocaleDateString()}
                                        {it.approved_at ? ` · Approved ${new Date(it.approved_at).toLocaleDateString()}` : ""}
                                        {it.session_bound_at ? ` · Synced ${new Date(it.session_bound_at).toLocaleDateString()}` : ""}
                                    </p>
                                    {Array.isArray(it.status_history) && it.status_history.length > 0 && (() => {
                                        const last = it.status_history[it.status_history.length - 1];
                                        const verb = {
                                            approve: "Approved", restore: "Restored",
                                            deny: "Denied", suspend: "Suspended",
                                        }[last.action] || last.to;
                                        return (
                                            <p className="text-xs text-stage_late-dark mt-1" data-testid={`admin-audit-${it.id}`}>
                                                {verb} {new Date(last.at).toLocaleDateString()} by {last.by || "admin"}
                                                {last.notes ? <span className="text-[#6E7A74]"> — {last.notes}</span> : null}
                                            </p>
                                        );
                                    })()}
                                </div>
                                <div className="flex flex-wrap gap-2 items-center">
                                    {it.status === "waitlist" && (
                                        <>
                                            <button onClick={() => act(it.id, "approve")} data-testid={`admin-approve-${it.id}`}
                                                className="inline-flex items-center gap-1.5 bg-teal text-white hover:bg-teal-dark rounded-2xl px-4 py-2 text-sm font-medium">
                                                <Check size={14} strokeWidth={2} /> Approve
                                            </button>
                                            <button onClick={() => act(it.id, "deny")} data-testid={`admin-deny-${it.id}`}
                                                className="inline-flex items-center gap-1.5 bg-coral-light text-coral-dark hover:bg-coral-softer rounded-2xl px-4 py-2 text-sm font-medium">
                                                <X size={14} strokeWidth={2} /> Deny
                                            </button>
                                        </>
                                    )}
                                    {it.status === "approved" && (
                                        <>
                                            <button onClick={() => copyLoginInfo(it)} data-testid={`admin-copy-${it.id}`}
                                                className="inline-flex items-center gap-1.5 bg-white border border-teal text-teal-dark hover:bg-teal-soft rounded-2xl px-4 py-2 text-sm font-medium">
                                                <Copy size={14} strokeWidth={2} /> Copy login info
                                            </button>
                                            <button onClick={() => setResetUser(it)} data-testid={`admin-reset-${it.id}`}
                                                className="inline-flex items-center gap-1.5 bg-white border border-yellow-dark text-yellow-dark hover:bg-yellow-soft rounded-2xl px-4 py-2 text-sm font-medium">
                                                <KeyRound size={14} strokeWidth={2} /> Reset password
                                            </button>
                                            <button onClick={() => act(it.id, "suspend")} data-testid={`admin-revoke-${it.id}`}
                                                title="Lock this user out without deleting their data"
                                                className="inline-flex items-center gap-1.5 bg-white border border-coral text-coral-dark hover:bg-coral-light rounded-2xl px-4 py-2 text-sm font-medium">
                                                <Ban size={14} strokeWidth={2} /> Suspend access
                                            </button>
                                        </>
                                    )}
                                    {(it.status === "denied" || it.status === "suspended") && (
                                        <button onClick={() => act(it.id, "approve")} data-testid={`admin-reapprove-${it.id}`}
                                            title="Restore this user's access"
                                            className="inline-flex items-center gap-1.5 bg-white border border-teal text-teal-dark hover:bg-teal-soft rounded-2xl px-4 py-2 text-sm font-medium">
                                            <Check size={14} strokeWidth={2} /> Restore access
                                        </button>
                                    )}
                                    {it.role !== "admin" && (
                                        <button onClick={() => setDeleteUser(it)} data-testid={`admin-delete-${it.id}`}
                                            title="Permanently delete this user and their data"
                                            className="inline-flex items-center gap-1.5 bg-white border border-emergency text-emergency-dark hover:bg-emergency-light rounded-2xl px-4 py-2 text-sm font-medium">
                                            <Trash2 size={14} strokeWidth={2} /> Delete
                                        </button>
                                    )}
                                    <button onClick={() => {
                                        setOpenId(isOpen ? null : it.id);
                                        setNoteDraft(it.admin_notes || "");
                                    }} data-testid={`admin-notes-toggle-${it.id}`}
                                        className="inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] text-[#4C5551] hover:bg-cream-dark rounded-2xl px-4 py-2 text-sm font-medium">
                                        <NotebookPen size={14} strokeWidth={2} /> Notes
                                    </button>
                                </div>
                            </div>

                            {isOpen && (
                                <div className="mt-4 border-t border-[#E2E8F0] pt-4">
                                    <textarea
                                        data-testid={`admin-notes-input-${it.id}`}
                                        value={noteDraft}
                                        onChange={e => setNoteDraft(e.target.value)}
                                        className="ml-input min-h-[90px] py-2.5 text-sm"
                                        placeholder="Internal notes — only admins see these…"
                                    />
                                    <div className="flex justify-end mt-2">
                                        <button onClick={() => saveNotes(it.id)} data-testid={`admin-notes-save-${it.id}`}
                                            className="ml-btn-primary !min-h-[40px] !py-1.5 !px-4 text-sm">Save note</button>
                                    </div>
                                </div>
                            )}
                        </article>
                    );
                })}
            </section>

            <div className="mt-10 ml-card p-5 bg-yellow-soft !border-yellow-soft" data-testid="admin-email-note">
                <div className="flex items-center gap-3">
                    <Mail strokeWidth={1.6} className="text-yellow-dark" />
                    <div>
                        <h3 className="font-medium text-yellow-dark">Manual login emails (for now)</h3>
                        <p className="text-sm text-[#1F2624]">
                            We&apos;re not sending automated emails yet. After approving someone,
                            click <strong>Copy login info</strong> on their row to grab a ready-made
                            message and paste it into your email of choice.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
